import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { Controller, type Control } from 'react-hook-form';
import { z } from 'zod';

import { nonFalsey } from '@/utils/nonFalsey';

const PostTagSchema = z.object({
  _id: z.string().nullable(),
  displayName: z.string().nullable(),
  optionDisplayName: z.string().nullable().optional(),
});

export const PostTopicSchema = z.object({
  tags: z.array(PostTagSchema),
});

export type PostTopicFormData = z.infer<typeof PostTopicSchema>;
export type PostTag = z.infer<typeof PostTagSchema>;

interface Props {
  control: Control<PostTopicFormData>;
  options: PostTag[];
  loading: boolean;
}

export const PostTopicsForm = ({ control, options, loading }: Props) => {
  const filter = createFilterOptions<PostTag>();

  const sortOptions = (options: PostTag[]) => {
    return (
      options.sort(
        (a, b) => a.displayName?.localeCompare(b.displayName ?? '') ?? 0,
      ) ?? []
    );
  };

  return (
    <Controller
      name="tags"
      control={control}
      render={({ field, fieldState }) => (
        <Autocomplete
          freeSolo
          fullWidth
          multiple
          size="small"
          data-tour-id="noticeboard-label-filter"
          id="noticeboard-label-filter"
          options={sortOptions(options)}
          disableCloseOnSelect
          loading={loading}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }

            if (option.optionDisplayName) {
              return option.optionDisplayName;
            }

            if (option.displayName) {
              return option.displayName;
            }

            return '';
          }}
          isOptionEqualToValue={(option, value) =>
            option.displayName === value.displayName
          }
          {...field}
          filterOptions={(options: PostTag[], params) => {
            const filtered = filter(options, params);

            const tagExists = options.some(
              (option) => option.displayName === params.inputValue,
            );

            if (params.inputValue !== '' && !tagExists) {
              filtered.push({
                _id: null,
                displayName: params.inputValue,
                optionDisplayName: `Add "${params.inputValue}" as a new topic`,
              });
            }

            sortOptions(filtered);

            return filtered;
          }}
          renderTags={(value, getTagProps) => {
            return (
              <Box sx={{ paddingY: 1 }}>
                {value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.displayName}
                    {...getTagProps({ index })}
                    key={option._id ?? option?.displayName ?? ''}
                  />
                ))}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="filled"
              disabled={loading}
              label="Update topics for post"
              placeholder="Type to search or add topics"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
          onChange={(_, value) => field.onChange(value.filter(nonFalsey))}
        />
      )}
    />
  );
};
